/**
 * v3.killList — /v3/kill-list/* (5 routes).
 * Workflow: create → verify → execute → /accounts/kill.
 */

import type {
  RequestOptions,
  ApiResult,
  GenericResult,
  KillListEntryDto,
  CreateKillListEntry,
  KillListVerifyInput,
  KillListExecuteInput,
  KillListCancelInput,
  KillListFilters,
  TestCleanupRunResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface KillListListResponse {
  success: boolean;
  count: number;
  entries: ReadonlyArray<KillListEntryDto>;
}

export interface KillListSingleResponse {
  success: boolean;
  entry?: KillListEntryDto;
  executionResult?: {
    accountKilled: boolean;
    jxChangeResponse?: Record<string, unknown>;
  };
  error?: string;
}

export class KillListApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: KillListFilters = {},
    opts?: RequestOptions
  ): Promise<KillListListResponse> {
    return this.http.get<KillListListResponse>('/v3/kill-list', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(entryId: string, opts?: RequestOptions): Promise<KillListSingleResponse> {
    return this.http.get<KillListSingleResponse>(
      `/v3/kill-list/${encodeURIComponent(entryId)}`,
      { ...opts }
    );
  }

  public create(
    req: CreateKillListEntry,
    opts?: RequestOptions
  ): Promise<KillListSingleResponse> {
    return this.http.post<KillListSingleResponse>('/v3/kill-list', { ...opts, body: req });
  }

  public verify(
    entryId: string,
    req: KillListVerifyInput,
    opts?: RequestOptions
  ): Promise<KillListSingleResponse> {
    return this.http.post<KillListSingleResponse>(
      `/v3/kill-list/${encodeURIComponent(entryId)}/verify`,
      { ...opts, body: req }
    );
  }

  public execute(
    entryId: string,
    req: KillListExecuteInput,
    opts?: RequestOptions
  ): Promise<KillListSingleResponse> {
    return this.http.post<KillListSingleResponse>(
      `/v3/kill-list/${encodeURIComponent(entryId)}/execute`,
      { ...opts, body: req }
    );
  }

  /**
   * The officer is done testing: release and run every held kill of this
   * entry's test application in kill order (Bill 2026-09-15). The server
   * re-derives that the application is a test application.
   */
  public releaseTestCleanup(
    entryId: string,
    executedByEmail: string,
    opts?: RequestOptions
  ): Promise<TestCleanupRunResponse> {
    return this.http.post<TestCleanupRunResponse>(
      `/v3/kill-list/${encodeURIComponent(entryId)}/execute`,
      { ...opts, body: { executedByEmail, releaseTestCleanup: true } }
    );
  }

  public cancel(
    entryId: string,
    req: KillListCancelInput,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/v3/kill-list/${encodeURIComponent(entryId)}/cancel`,
      { ...opts, body: req }
    );
  }
}

export type { ApiResult };
